import { ReactComponent as PlayButtonSVG } from '@/assets/images/svg/buttons/play-button.svg';
import Image from '@/components/Image';
import YouTubeVideo from '@/components/YouTubeVideo';
import Modal from '@/components/modals/Modal';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { mediaBreakpointUp } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import styled from 'styled-components';

const COLOR_GRAY_HIGHLIGHT = '#e3e3e3';

export const EventCard = styled.div`
  max-width: ${remCalc(996)};
  border-radius: ${remCalc(12)};
  background-color: ${COLORS.WHITE};
  box-shadow: 0px 0px 30px 0px ${hexToRGBA(COLORS.BLACK, 0.1)};
  margin: 0 auto ${remCalc(40)} auto;

  ${mediaBreakpointUp('tablet')} {
    margin-bottom: ${remCalc(60)};
  }

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    margin-bottom: ${remCalc(-30)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    margin-bottom: ${remCalc(-60)};
  }
`;

export const EventDetail = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(30)};
  align-items: flex-start;
  align-content: flex-start;
  row-gap: ${remCalc(30)};
  margin: 0 auto ${remCalc(40)} auto;

  ${mediaBreakpointUp('tablet')} {
    margin-bottom: ${remCalc(60)};
  }

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    margin-bottom: ${remCalc(80)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    margin-bottom: ${remCalc(120)};
  }
`;

export const SideOne = styled.div`
  width: 100%;
  padding: ${remCalc(30, 30, 0, 30)};

  ${mediaBreakpointUp('desktop')} {
    padding-bottom: ${remCalc(30)};
    padding-right: ${remCalc(24)};
    width: ${remCalc(364)};
  }

  ${mediaBreakpointUp('desktop')} {
    width: ${remCalc(404)};
  }
`;

export const SideTwo = styled.div`
  border-top: 1px solid ${COLOR_GRAY_HIGHLIGHT};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${remCalc(30)};
  flex: 1 0 0;
  padding: ${remCalc(30)};

  ${mediaBreakpointUp('desktop')} {
    border-left: 1px solid ${COLOR_GRAY_HIGHLIGHT};
    border-top: 0px;
    padding-top: ${remCalc(30)};
  }
`;

export const EventTitle = styled.h3`
  ${customTextSize(24, 30)};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  padding-bottom: ${remCalc(16)};

  strong {
    background: linear-gradient(90deg, #0f52ff 0.52%, #a024ff 48.17%, #ff2b73 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(32, 40)};
  }
`;

export const EventDescription = styled.div`
  ${customTextSize(16, 24)};

  ul {
    list-style-type: disc;
    list-style-position: outside;
    padding: ${remCalc(20, 0, 0, 32)};
  }
  em {
    font-style: italic;
  }
`;

export const SpeakerBlock = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(30)};

  ${mediaBreakpointUp('desktop')} {
    row-gap: ${remCalc(32)};
  }
`;

export const VideoModalYouTubeVideo = styled(YouTubeVideo)`
  ${mediaBreakpointUp('desktopWide')} {
    height: 100%;
    padding-bottom: 0;
  }
`;

export const VideoModal = styled(Modal)`
  ${mediaBreakpointUp('desktopWide')} {
    --modal-height: 100%;
  }
`;

export const PlayButton = styled(PlayButtonSVG)`
  position: absolute;
  top: calc(50% - ${remCalc(48 / 2)});
  left: calc(50% - ${remCalc(48 / 2)});
  width: ${remCalc(48)};
  z-index: 2;
  transform: scale(1);
  transform-origin: center;
  transition: transform 0.4s ease-in-out, box-shadow 0.4s ease-in-out;
  box-shadow: 0 0 30px 10px ${hexToRGBA(COLORS.BLACK, 0.25)};
  border-radius: 100%;
  pointer-events: none;
  background-color: ${COLORS.WHITE};

  ${mediaBreakpointUp('tablet')} {
    top: calc(50% - ${remCalc(80 / 2)});
    left: calc(50% - ${remCalc(80 / 2)});
    width: ${remCalc(80)};
  }

  ${mediaBreakpointUp('desktop')} {
    top: calc(50% - ${remCalc(120 / 2)});
    left: calc(50% - ${remCalc(120 / 2)});
    width: ${remCalc(120)};
  }
`;

export const PlayButtonSmall = styled(PlayButton)`
  ${mediaBreakpointUp('desktop')} {
    top: calc(50% - ${remCalc(80 / 2)});
    left: calc(50% - ${remCalc(80 / 2)});
    width: ${remCalc(80)};
  }
`;

export const VideoWrapper = styled.div`
  position: relative;
  &:hover {
    ${PlayButton} {
      transform: scale(1.1);
    }
    cursor: pointer;
  }

  > video {
    border-radius: ${remCalc(3)};
  }
  > img {
    object-fit: contain;
    width: 100%;
  }
`;

export const Anchor = styled.a`
  display: block;
  position: relative;
  top: -100px;
  visibility: hidden;
`;

export const VideoThumb = styled(Image)<{ onTop: boolean }>`
  border-radius: ${(props) => (props.onTop ? '0.75rem 0.75rem 0 0' : '0.75rem')};
`;

const frontlinesEventComponents = {
  EventCard,
  EventDetail,
  SideOne,
  SideTwo,
  EventTitle,
  EventDescription,
  SpeakerBlock,
  VideoModalYouTubeVideo,
  VideoModal,
  VideoWrapper,
  PlayButton,
  PlayButtonSmall,
  Anchor,
  VideoThumb,
};

export default frontlinesEventComponents;
